import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Purchase() {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    amount: location.state?.amount || '300',
    currency: location.state?.currency || 'USD',
    name: '',
    email: '',
    walletAddress: '',
    couponCode: '',
    paymentMethod: 'peska'
  });
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [usdtAmount, setUsdtAmount] = useState('300.00');
  const [walletError, setWalletError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');

  // Format number with commas
  const formatNumber = (num: string | number): string => {
    const numStr = typeof num === 'number' ? num.toString() : num;
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const currencies = [
    { symbol: 'USD', name: 'US Dollar', icon: 'https://asset.xcoins.com/5ea769c4-4771-4ea6-bd65-16f97a212e7b.svg' },
    { symbol: 'EUR', name: 'Euro', icon: 'https://asset.xcoins.com/f039066a-c440-425d-a35c-c49cdd3b4f7f.svg' },
    { symbol: 'JPY', name: 'Japanese Yen', icon: 'https://hatscripts.github.io/circle-flags/flags/jp.svg' },
    { symbol: 'GBP', name: 'British Pound', icon: 'https://asset.xcoins.com/ecc40404-ad7c-44f7-b98a-c3b131f83b01.svg' },
    { symbol: 'CAD', name: 'Canadian Dollar', icon: 'https://hatscripts.github.io/circle-flags/flags/ca.svg' }
  ];

  const paymentMethods = [
    { id: 'peska', name: 'Peska', icon: 'ri-bank-line' }
  ];

  // Fetch exchange rates
  const fetchExchangeRates = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      if (!response.ok) {
        throw new Error('API response not ok');
      }
      const data = await response.json();
      const markedUpRates: Record<string, number> = {};
      const markupRate = 1 + (10.1 - couponDiscount) / 100; // Apply coupon discount
      Object.keys(data.rates).forEach(currency => {
        markedUpRates[currency] = data.rates[currency] * markupRate;
      });
      setExchangeRates(markedUpRates);
    } catch (error) {
      // Silently use fallback rates with markup if API fails
      const markupRate = 1 + (10.1 - couponDiscount) / 100;
      setExchangeRates({
        USD: 1 * markupRate,
        EUR: 0.9224 * markupRate,
        JPY: 119.52 * markupRate,
        GBP: 0.7945 * markupRate,
        CAD: 1.3599 * markupRate
      });
    }
  };

  // Validate coupon code
  const validateCouponCode = async (code: string) => {
    if (!code.trim()) {
      setCouponDiscount(0);
      setCouponError('');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('agents')
        .select('fee_percentage')
        .eq('agent_name', code.trim())
        .single();

      if (error || !data) {
        setCouponDiscount(0);
        setCouponError('Invalid coupon code');
        return;
      }

      setCouponDiscount(data.fee_percentage || 0);
      setCouponError('');
    } catch (error) {
      console.warn('Error validating coupon:', error);
      setCouponDiscount(0);
      setCouponError('Invalid coupon code');
    }
  };

  // Calculate USDT amount
  const calculateUSDTAmount = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount) || 0;
    
    const rate = exchangeRates[currency];
    if (rate) {
      const usdAmount = numAmount / rate;
      return usdAmount.toFixed(2);
    }
    
    return '0.00';
  };

  // Validate email address
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate TRC20 wallet address
  const validateTRC20Address = (address: string): boolean => {
    // TRC20 addresses start with 'T' and are 34 characters long
    const trc20Regex = /^T[A-Za-z1-9]{33}$/;
    return trc20Regex.test(address);
  };

  useEffect(() => {
    fetchExchangeRates();
  }, [couponDiscount]);

  useEffect(() => {
    if (Object.keys(exchangeRates).length > 0) {
      setUsdtAmount(calculateUSDTAmount(formData.amount, formData.currency));
    }
  }, [formData.amount, formData.currency, exchangeRates]);

  const handleInputChange = async (field: string, value: string) => {
    // Name field: allow only English letters, spaces, hyphens
    if (field === 'name') {
      const englishOnly = value.replace(/[^a-zA-Z\s\-']/g, '');
      setFormData(prev => ({ ...prev, [field]: englishOnly }));
      if (value !== englishOnly) {
        setNameError('Please enter your name in English only.');
      } else {
        setNameError('');
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate email address in real-time
    if (field === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
    
    // Validate wallet address in real-time
    if (field === 'walletAddress') {
      if (value && !validateTRC20Address(value)) {
        setWalletError('Invalid TRC20 address. Must start with "T" and be 34 characters long.');
      } else {
        setWalletError('');
      }
    }

    // Validate coupon code
    if (field === 'couponCode') {
      await validateCouponCode(value);
    }
  };

  const handleNextStep = () => {
    if (step === 1 && (!formData.amount || parseFloat(formData.amount) <= 0)) {
      alert('Please enter a valid amount');
      return;
    }
    if (step === 2) {
      if (!formData.name || !formData.email || !formData.walletAddress) {
        alert('Please fill in all required fields');
        return;
      }
      if (!validateEmail(formData.email)) {
        alert('Please enter a valid email address');
        return;
      }
      if (!validateTRC20Address(formData.walletAddress)) {
        alert('Please enter a valid TRC20 wallet address');
        return;
      }
    }
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    if (step < 4) {
      setStep(4); // Move to confirmation screen
    }
  };

  const handleConfirmPurchase = async () => {
    try {
      // Remove commas before sending
      const cleanAmount = formData.amount.replace(/,/g, '');
      const cleanUsdtAmount = usdtAmount.replace(/,/g, '');

      // Prepare order data
      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        amount: parseFloat(cleanAmount),
        currency: formData.currency,
        usdt_amount: parseFloat(cleanUsdtAmount),
        wallet_address: formData.walletAddress,
        referral_code: formData.couponCode || null,
        payment_method: 'Peska',
        status: 'pending'
      };

      console.log('Sending order data:', orderData);

      // Call Supabase Edge Function to save to database and send email
      const response = await fetch('https://xaqgqyeqznumoihnasok.supabase.co/functions/v1/send-order-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      
      const result = await response.json();
      console.log('Response data:', result);

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (result.success) {
        alert('購入が完了しました！確認メールを送信しました。');
        window.REACT_APP_NAVIGATE('/');
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error processing order:', error);
      alert(`エラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    }
  };

  const getCurrentCurrencyIcon = () => {
    return currencies.find(c => c.symbol === formData.currency)?.icon || currencies[0].icon;
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <main className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((stepNum) => (
                <div key={stepNum} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-lg md:text-xl transition-all ${
                        step >= stepNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {step > stepNum ? (
                        <i className="ri-check-line"></i>
                      ) : (
                        stepNum
                      )}
                    </div>
                    <span className={`text-xs md:text-sm mt-2 whitespace-nowrap ${step >= stepNum ? 'text-blue-400' : 'text-gray-500'}`}>
                      {stepNum === 1 && 'Amount'}
                      {stepNum === 2 && 'Details'}
                      {stepNum === 3 && 'Payment'}
                      {stepNum === 4 && 'Confirm'}
                    </span>
                  </div>
                  {stepNum < 4 && (
                    <div className={`w-8 md:w-16 h-0.5 mx-1 md:mx-2 ${
                      step > stepNum ? 'bg-blue-600' : 'bg-gray-600'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Card className="p-8">
            {/* Step 1: Amount Selection */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">Select Amount</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount to spend
                  </label>
                  <div className="flex items-center bg-gray-700 rounded-lg p-4">
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="flex-1 min-w-0 bg-transparent text-white text-2xl font-semibold outline-none"
                      placeholder="0"
                    />
                    <div className="flex items-center space-x-1 md:space-x-2 ml-2 md:ml-3 flex-shrink-0">
                      <img 
                        src={getCurrentCurrencyIcon()} 
                        alt={formData.currency} 
                        className="w-5 h-5 md:w-6 md:h-6"
                      />
                      <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                        className="bg-gray-600 text-white font-medium px-2 py-1 md:px-3 md:py-2 rounded-lg outline-none cursor-pointer pr-8 text-sm md:text-base"
                      >
                        {currencies.map(currency => (
                          <option key={currency.symbol} value={currency.symbol}>
                            {currency.symbol}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center py-4">
                  <div className="bg-gray-700 p-3 rounded-full">
                    <i className="ri-arrow-down-line text-white text-xl"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    You will receive
                  </label>
                  <div className="flex items-center bg-gray-700 rounded-lg p-4">
                    <input
                      type="text"
                      value={usdtAmount}
                      readOnly
                      className="flex-1 min-w-0 bg-transparent text-white text-2xl font-semibold outline-none"
                    />
                    <div className="flex items-center space-x-1 md:space-x-2 ml-2 md:ml-3 flex-shrink-0">
                      <img 
                        src="https://static.readdy.ai/image/75eaeb23002452c01ddd137892cf07ae/149ac95f3d8090d1ea253f9967993066.png" 
                        alt="USDT" 
                        className="w-5 h-5 md:w-6 md:h-6"
                      />
                      <span className="text-white font-medium text-sm md:text-base">USDT</span>
                    </div>
                  </div>
                </div>

                <Button size="lg" fullWidth onClick={handleNextStep}>
                  Continue
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </div>
            )}

            {/* Step 2: User Details */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">Enter Your Details</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 text-sm ${
                      nameError ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    placeholder="Your full name (English only)"
                  />
                  {nameError && (
                    <p className="text-xs text-red-400 mt-2">
                      <i className="ri-error-warning-line mr-1"></i>
                      {nameError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 text-sm ${
                      emailError ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    placeholder="your@email.com"
                  />
                  {emailError && (
                    <p className="text-xs text-red-400 mt-2">
                      <i className="ri-error-warning-line mr-1"></i>
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    USDT Wallet Address (TRC20) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.walletAddress}
                    onChange={(e) => handleInputChange('walletAddress', e.target.value)}
                    className={`w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 text-sm font-mono ${
                      walletError ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    placeholder="T..."
                  />
                  {walletError && (
                    <p className="text-xs text-red-400 mt-2">
                      <i className="ri-error-warning-line mr-1"></i>
                      {walletError}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    <i className="ri-information-line mr-1"></i>
                    Only TRC20 network is accepted. Make sure this is your correct TRC20 USDT wallet address. Transactions cannot be reversed.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Coupon Code (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.couponCode}
                    onChange={(e) => handleInputChange('couponCode', e.target.value)}
                    className={`w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 text-sm ${
                      couponError ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    placeholder="Enter coupon code if you have one"
                  />
                  {couponError && (
                    <p className="text-xs text-red-400 mt-2">
                      <i className="ri-error-warning-line mr-1"></i>
                      {couponError}
                    </p>
                  )}
                </div>

                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-shield-check-line text-blue-400 text-xl mt-0.5"></i>
                    <div>
                      <h4 className="text-white font-medium text-sm mb-1">Secure Transaction</h4>
                      <p className="text-gray-400 text-xs">Your information is encrypted and secure. We never share your data with third parties.</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2 md:space-x-4">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    fullWidth 
                    onClick={handlePreviousStep}
                    className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base"
                  >
                    <i className="ri-arrow-left-line mr-1 md:mr-2"></i>
                    Back
                  </Button>
                  <Button 
                    size="lg" 
                    fullWidth 
                    onClick={handleNextStep}
                    className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-base"
                  >
                    Continue
                    <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Payment Method */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">Select Payment Method</h2>
                
                <div className="space-y-3">
                  {paymentMethods.map(method => (
                    <button
                      key={method.id}
                      onClick={() => handleInputChange('paymentMethod', method.id)}
                      className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        formData.paymentMethod === method.id
                          ? 'border-blue-600 bg-blue-900/20'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${
                          formData.paymentMethod === method.id ? 'bg-blue-600' : 'bg-gray-700'
                        }`}>
                          <i className={`${method.icon} text-2xl text-white`}></i>
                        </div>
                        <span className="text-white font-medium">{method.name}</span>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        formData.paymentMethod === method.id
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-600'
                      }`}>
                        {formData.paymentMethod === method.id && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Order Summary */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">You spend</span>
                      <span className="text-white font-medium">{formatNumber(formData.amount)} {formData.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">You receive</span>
                      <span className="text-white font-medium">{formatNumber(usdtAmount)} USDT</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-white">Total</span>
                        <span className="text-white">{formatNumber(formData.amount)} {formData.currency}</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="flex space-x-2 md:space-x-4">
                  <Button variant="outline" size="lg" fullWidth onClick={handlePreviousStep} className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-base">
                    <i className="ri-arrow-left-line mr-1 md:mr-2"></i>
                    Back
                  </Button>
                  <Button size="lg" fullWidth onClick={handleSubmit} className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-base">
                    <i className="ri-secure-payment-line mr-1 md:mr-2"></i>
                    Check
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-list-3-line text-3xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Confirm Your Purchase</h2>
                  <p className="text-gray-400">Please review your order details before completing the purchase</p>
                </div>

                {/* Purchase Details */}
                <Card className="p-6 bg-gray-800">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-shopping-bag-line mr-2"></i>
                    Purchase Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-gray-700">
                      <span className="text-gray-400">Amount to spend</span>
                      <span className="text-white font-medium">{formatNumber(formData.amount)} {formData.currency}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-700">
                      <span className="text-gray-400">USDT to receive</span>
                      <span className="text-white font-medium">{formatNumber(usdtAmount)} USDT</span>
                    </div>
                    <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 flex items-start space-x-2">
                      <i className="ri-alert-line text-yellow-400 text-lg mt-0.5 flex-shrink-0"></i>
                      <p className="text-yellow-200 text-xs leading-relaxed">
                        Please note that the exchange rate may change depending on the timing of the transfer.<br />
                        We will transfer within 5 business days after confirming your deposit.<br />
                        Please note that responses may be delayed on weekends and public holidays.
                      </p>
                    </div>
                    <div className="flex justify-between py-3">
                      <span className="text-white font-semibold text-lg">Total Payment</span>
                      <span className="text-blue-400 font-bold text-lg">{formatNumber(formData.amount)} {formData.currency}</span>
                    </div>
                  </div>
                </Card>

                {/* Personal Information */}
                <Card className="p-6 bg-gray-800">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-gray-700">
                      <span className="text-gray-400">Name</span>
                      <span className="text-white font-medium">{formData.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-700">
                      <span className="text-gray-400">Email</span>
                      <span className="text-white font-medium">{formData.email}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-400">Wallet Address</span>
                      <span className="text-white font-medium font-mono text-sm break-all">{formData.walletAddress}</span>
                    </div>
                    {formData.couponCode && (
                      <div className="flex justify-between py-2 border-t border-gray-700">
                        <span className="text-gray-400">Coupon Code</span>
                        <span className="text-green-400 font-medium">{formData.couponCode}</span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Payment Method */}
                <Card className="p-6 bg-gray-800">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-bank-card-line mr-2"></i>
                    Payment Method
                  </h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-600">
                      <i className="ri-bank-line text-2xl text-white"></i>
                    </div>
                    <span className="text-white font-medium">Peska</span>
                  </div>
                </Card>

                {/* Important Notice */}
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-alert-line text-yellow-400 text-xl mt-0.5"></i>
                    <div>
                      <h4 className="text-yellow-400 font-medium text-sm mb-1">Important Notice</h4>
                      <p className="text-gray-300 text-xs">Please verify all information is correct. Cryptocurrency transactions cannot be reversed once completed.</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <Button variant="outline" size="lg" fullWidth onClick={handlePreviousStep} className="px-3 py-2 text-xs sm:text-sm">
                    <i className="ri-arrow-left-line mr-1"></i>
                    Back
                  </Button>
                  <Button size="lg" fullWidth onClick={handleConfirmPurchase} className="px-3 py-2 text-xs sm:text-sm">
                    <i className="ri-secure-payment-line mr-1"></i>
                    Complete
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Security Notice */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center space-x-6 text-gray-400 text-sm">
              <div className="flex items-center space-x-2">
                <i className="ri-lock-line"></i>
                <span>SSL Encrypted</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-line"></i>
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-customer-service-line"></i>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}